import { useMemo } from "react";
import {
  useFetchActions,
  isActionCompleted,
  ONBOARDING_ACTION_TYPES,
  ACTION_STATUS,
  ACTION_TYPE_SLUG,
} from "@core/features/actions";
import type {
  OnboardingStepDefinition,
  OnboardingStep,
  SubStepDefinition,
  UseOnboardingActionsArgs,
  UseOnboardingActionsResult,
} from "../onboarding.types";

/** Org admin onboarding step slugs — used to classify flows */
const ORG_ONBOARDING_SLUGS: string[] = [
  ACTION_TYPE_SLUG.ORG_DISPLAY_NAME,
  ACTION_TYPE_SLUG.ORG_ONBOARDING_QUIZ,
  ACTION_TYPE_SLUG.ORG_UPLOAD_LOGO,
  ACTION_TYPE_SLUG.ORG_UPDATE_SOCIALS,
];

/**
 * Hook to fetch, filter, and sort onboarding actions for a given program.
 *
 * Finds all incomplete onboarding-type actions for the program and builds
 * step data from each action's display_options.onboarding_steps configuration.
 *
 * @example
 * ```tsx
 * const {
 *   steps,
 *   currentStep,
 *   allRequiredStepsCompleted,
 *   isLoading,
 * } = useOnboardingActions({ programId, user });
 *
 * if (isLoading) return <Spinner />;
 *
 * return (
 *   <OnboardingStepper
 *     steps={steps}
 *     currentStepIndex={currentStepIndex}
 *   />
 * );
 * ```
 */
export function useOnboardingActions({
  programId,
  user,
  flowType,
}: UseOnboardingActionsArgs): UseOnboardingActionsResult {
  const { data, isLoading } = useFetchActions({
    programId,
    user,
    isEnabled: !!programId && !!user,
  });

  // Find ALL onboarding actions for this program (including completed)
  // User may have multiple 'onboarding' actions (e.g., participant + admin)
  const onboardingActions = useMemo(() => {
    if (!data?.actions) return [];

    return data.actions.filter(
      (a) =>
        ONBOARDING_ACTION_TYPES.includes(a.action_type?.slug ?? "") &&
        a.program === programId
    );
  }, [data?.actions, programId]);

  // Build onboarding flows from onboarding actions
  const onboardingFlows = useMemo(() => {
    return onboardingActions.map((onboardingAction) => {
      // Get step definitions from action's display_options
      const displayOptions = onboardingAction.display_options as {
        onboarding_steps?: OnboardingStepDefinition[];
      } | null;
      const stepDefs = displayOptions?.onboarding_steps ?? [];
      const sortedDefs = [...stepDefs].sort((a, b) => a.sequence - b.sequence);

      // Match definitions with actual step actions
      const steps: OnboardingStep[] = sortedDefs.map((def) => {
        const stepAction = data?.actions?.find(
          (a) => a.action_type?.slug === def.slug && a.program === programId
        );

        // Enrich sub-steps with their actions (if action_slug is specified)
        const enrichedSubSteps: SubStepDefinition[] | undefined =
          def.sub_steps?.map((subStep) => ({
            ...subStep,
            action: subStep.action_slug
              ? data?.actions?.find(
                  (a) =>
                    a.action_type?.slug === subStep.action_slug &&
                    a.program === programId
                )
              : undefined,
          }));

        return {
          slug: def.slug,
          sequence: def.sequence,
          required: def.required,
          name: def.name,
          sub_steps: enrichedSubSteps,
          action: stepAction,
          isCompleted: stepAction ? isActionCompleted(stepAction) : false,
        };
      });

      // Find first incomplete step
      const currentStepIndex = steps.findIndex((s) => !s.isCompleted);
      const allRequiredCompleted = steps
        .filter((s) => s.required)
        .every((s) => s.isCompleted);

      return {
        onboardingAction,
        type: onboardingAction.action_type?.slug ?? "",
        steps,
        currentStepIndex:
          currentStepIndex >= 0 ? currentStepIndex : steps.length,
        currentStep: currentStepIndex >= 0 ? steps[currentStepIndex] : null,
        allRequiredCompleted,
      };
    });
  }, [onboardingActions, data?.actions, programId]);

  // Filter flows by flowType when provided
  const filteredFlows = useMemo(() => {
    if (!flowType) return onboardingFlows;

    return onboardingFlows.filter((flow) => {
      const steps = flow.steps;
      const hasOrgSteps = steps.some((s) =>
        ORG_ONBOARDING_SLUGS.includes(s.slug)
      );
      return flowType === "orgAdmin" ? hasOrgSteps : !hasOrgSteps;
    });
  }, [onboardingFlows, flowType]);

  // Get the first onboarding flow for simple UIs
  const currentFlow = filteredFlows[0] ?? null;

  // Pass through only when ALL onboarding actions are completed or in progress.
  // A newly issued onboarding (e.g., participant invite claimed by an existing
  // org admin) will have "Action Issued" status and must NOT be skipped.
  const allOnboardingsComplete =
    onboardingActions.length === 0 ||
    onboardingActions.every((action) => {
      const status = action.current_status?.name;
      return status === ACTION_STATUS.COMPLETED || status === ACTION_STATUS.IN_PROGRESS;
    });

  return {
    // Multiple flows (filtered by flowType when provided)
    onboardingFlows: filteredFlows,
    hasMultipleFlows: filteredFlows.length > 1,

    // Single flow convenience (for simple stepper UI)
    currentFlow,
    steps: currentFlow?.steps ?? [],
    currentStep: currentFlow?.currentStep ?? null,
    currentStepIndex: currentFlow?.currentStepIndex ?? 0,
    allRequiredStepsCompleted: currentFlow?.allRequiredCompleted ?? false,

    // Overall state
    allOnboardingsComplete,
    isLoading,
  };
}
